// pptxgenjs ships Node-only `import("node:fs")` calls that webpack 5 (CRA) can't
// bundle, so its prebuilt browser bundle (copied to public/vendor from
// node_modules/pptxgenjs/dist/pptxgen.bundle.js) is loaded on demand instead.
const loadPptxGenJS = () =>
  new Promise((resolve, reject) => {
    if (window.PptxGenJS) return resolve(window.PptxGenJS);

    const script = document.createElement("script");
    script.src = `${process.env.PUBLIC_URL || ""}/vendor/pptxgen.bundle.js`;
    script.async = true;
    script.onload = () =>
      window.PptxGenJS
        ? resolve(window.PptxGenJS)
        : reject(new Error("PptxGenJS introuvable après chargement"));
    script.onerror = () =>
      reject(new Error("Impossible de charger pptxgen.bundle.js"));
    document.body.appendChild(script);
  });

const CHART_COLORS = [
  "4318FF",
  "6AD2FF",
  "EFF4FB",
  "01B574",
  "FFB547",
  "E31A1A",
  "8F9BBA",
  "A3AED0",
  "7551FF",
  "39B8FF",
];

const NAVY = "1B2559";
const GRAY = "707EAE";

/**
 * Exports a pie chart (plus the full data table) to a .pptx file.
 * The chart is a native PowerPoint chart, so it stays editable.
 *
 * @param {Object}   opts
 * @param {string}   opts.title      Slide title
 * @param {string}   opts.subtitle   Filters summary shown under the title
 * @param {string[]} opts.labels     Slice labels (full list)
 * @param {number[]} opts.series     Slice values (full list)
 * @param {number}   [opts.chartLimit=10] Slices shown in the chart (matches the on-screen chart)
 * @param {string}   opts.fileName   Output file name (without extension)
 */
export const exportPieToPptx = async ({
  title,
  subtitle,
  labels,
  series,
  chartLimit = 10,
  fileName,
}) => {
  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE"; // 13.33" x 7.5"
  pptx.title = title;

  const total = series.reduce((a, b) => a + b, 0);

  // Slide 1 — pie chart
  const chartSlide = pptx.addSlide();
  chartSlide.addText(title, {
    x: 0.5,
    y: 0.3,
    w: 12.3,
    h: 0.6,
    fontSize: 26,
    bold: true,
    color: NAVY,
  });
  chartSlide.addText(`${subtitle}  •  Total : ${total}`, {
    x: 0.5,
    y: 0.9,
    w: 12.3,
    h: 0.4,
    fontSize: 14,
    color: GRAY,
  });

  chartSlide.addChart(
    pptx.charts.PIE,
    [
      {
        name: title,
        labels: labels.slice(0, chartLimit),
        values: series.slice(0, chartLimit),
      },
    ],
    {
      x: 0.5,
      y: 1.5,
      w: 12.3,
      h: 5.6,
      chartColors: CHART_COLORS,
      showLegend: true,
      legendPos: "r",
      legendFontSize: 12,
      legendColor: NAVY,
      showPercent: true,
      showValue: false,
      showLabel: false,
      dataLabelFontSize: 12,
      dataLabelColor: "000000",
      dataLabelFormatCode: "0.0%",
      dataBorder: { pt: 1, color: "FFFFFF" },
    }
  );

  // Slide 2+ — full data table (paginated automatically)
  if (labels.length > 0) {
    const tableSlide = pptx.addSlide();
    tableSlide.addText(`${title} — détail`, {
      x: 0.5,
      y: 0.3,
      w: 12.3,
      h: 0.5,
      fontSize: 22,
      bold: true,
      color: NAVY,
    });

    const headerCell = (text, align = "left") => ({
      text,
      options: {
        bold: true,
        color: "FFFFFF",
        fill: { color: "4318FF" },
        align,
      },
    });

    const rows = [
      [
        headerCell("#", "center"),
        headerCell("Libellé"),
        headerCell("Nombre", "center"),
        headerCell("%", "center"),
      ],
      ...labels.map((label, index) => {
        const value = series[index];
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
        return [
          { text: String(index + 1), options: { align: "center" } },
          { text: String(label) },
          { text: String(value), options: { align: "center" } },
          { text: `${pct}%`, options: { align: "center" } },
        ];
      }),
    ];

    tableSlide.addTable(rows, {
      x: 0.5,
      y: 1,
      w: 12.3,
      colW: [0.8, 7.5, 2, 2],
      fontSize: 12,
      color: NAVY,
      border: { type: "solid", pt: 0.5, color: "E0E5F2" },
      autoPage: true,
      autoPageRepeatHeader: true,
      autoPageHeaderRows: 1,
      newSlideStartY: 0.5,
    });
  }

  await pptx.writeFile({ fileName: `${fileName}.pptx` });
};
